/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useState } from "react"
import { Box, Button, Typography } from "@mui/material"
import { useNavigate, useSearchParams } from "react-router-dom"
import { jwtDecode } from "jwt-decode"
import { useDispatch, useSelector } from "react-redux"
import {
  AuthSubtitle,
  AuthTitle,
  ChangeNumberText,
  CodeTitle,
  ContentContainer,
  RepeatCodeTitle,
  ResendText,
  StyledMuiOtp
} from "./styled"
import LoadingOverlay from "../../components/LoadingOverlay"

import { useAuth } from "./hooks/useAuth"
import { formatPhoneNumber } from "../../utils/formatters"
import { useResendOtpAuthRequest } from "../../api/hooks/useResendOtpAuthRequest"
import { useOtpAuthRequest } from "../../api/hooks/useOtpAuthRequest2"
import { IExchangeJwtPayload, IJwtPayload } from "./hooks/interface"
import { QUERY_PARAMS_EXCHANGE_TOKEN_KEY, ROUTES_PATHS } from "../../utils/constants"
import { ApiErrorCodes, AuthOtpResults, RequestOtpResults } from "../../api/interface"
import { setCustomerId, setPhoneNumber, setToken } from "../../store/slices/auth"
import { RootState } from "../../store/store"
import { useGetUserInfo } from "../../api/hooks/useGetUserInfo"
import { EUserIdentificationLevel } from "../../types/identification"

type AuthOtpConfirmationProps = {
  redirectUrlProp?: string
}

const AuthOtpConfirmation = ({ redirectUrlProp }: AuthOtpConfirmationProps) => {
  const { isLoading: isAuthLoading } = useAuth(redirectUrlProp)

  const [otp, setOtp] = useState("")
  const [timer, setTimer] = useState(60)
  const [otpState, setOtpState] = useState(2)
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState("")

  const {
    data: otpResult,
    mutate: otpMutate,
    status: otpAuthStatus,
  } = useOtpAuthRequest()
  const {
    data: resendOtpResult,
    mutate: resendOtpMutate,
    status: resendOtpStatus,
  } = useResendOtpAuthRequest()
  const {
    data: userInfo,
    isError: isUserInfoError,
    refetch: fetchUserInfo,
  } = useGetUserInfo(false)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()

  const initPath = useSelector((state: RootState) => state.auth.initPath)

  const redirectUrl = searchParams.get("redirectUrl")

  useEffect(() => {
    let timerId: NodeJS.Timeout
    if (otpState === 2 && timer > 0) {
      timerId = setInterval(() => setTimer((prev) => prev - 1), 1000)
    } else if (timer === 0) {
      setOtpState(1)
    }
    return () => clearInterval(timerId)
  }, [otpState, timer])

  useEffect(() => {
    if (otp.length === 6) {
      otpMutate({ otp })
    }
  }, [otp])

  useEffect(() => {
    if (otpAuthStatus === "error") {
      navigate(`/${ROUTES_PATHS.error}`)
      return
    }

    if (otpResult?.result === AuthOtpResults.ERROR) {
      if (
        otpResult.errorDetails?.code === null ||
        otpResult.errorDetails?.code === ApiErrorCodes.OTP_ATTEMPTS_EXCEEDED ||
        otpResult.errorDetails?.code === ApiErrorCodes.OTP_FLOW_FAILED
      ) {
        navigate(`/${ROUTES_PATHS.error}`)
        return
      }
      if (otpResult?.errorDetails?.code === ApiErrorCodes.OTP_ATTEMPT_FAILED) {
        setOtp("")
        return
      }
      navigate(`/${ROUTES_PATHS.authError}`)
      return
    }

    if (otpResult?.result === AuthOtpResults.AUTHORIZED) {
      if (otpResult.token === null) {
        navigate(`/${ROUTES_PATHS.error}`)
        return
      }

      const jwtToken = otpResult?.token
      if (jwtToken) {
        const payload = jwtDecode<IJwtPayload>(jwtToken)
        dispatch(setCustomerId(payload.CustomerId))
        dispatch(setPhoneNumber(payload.PhoneNumber))
        dispatch(setToken(jwtToken))

        setTimeout(() => {
          fetchUserInfo()
        }, 0)
      }
    }
  }, [otpResult])

  // check fields and redirect
  useEffect(() => {
    if (isUserInfoError) {
      navigate(`/${ROUTES_PATHS.error}`)
      return
    }

    if (userInfo) {
      const isPassportNeeded = userInfo.identificationLevel === EUserIdentificationLevel.Anonymous

      if (isPassportNeeded) {
        navigate(`/${ROUTES_PATHS.topUpMbankAccount}/identification/uprid`)
      } else if (redirectUrl || redirectUrlProp) {
        navigate(decodeURIComponent(redirectUrl || redirectUrlProp || ""))
      } else {
        navigate(initPath)
      }
    }
  }, [userInfo, navigate, searchParams])

  useEffect(() => {
    if (resendOtpStatus === "error") {
      navigate(`/${ROUTES_PATHS.error}`)
      return
    }
    if (resendOtpResult?.result === RequestOtpResults.SENT) {
      setOtpState(2)
      setTimer(60)
      return
    }
    if (resendOtpResult?.result === RequestOtpResults.WAIT) {
      setOtpState(2)
      setTimer(10)
      return
    }
    if (resendOtpResult?.result === RequestOtpResults.ERROR) {
      if (resendOtpResult?.errorDetails?.code === ApiErrorCodes.OTP_ATTEMPTS_EXCEEDED) {
        setOtpState(3)
        return
      }
      navigate(`/${ROUTES_PATHS.error}`)
    }
  }, [resendOtpResult, resendOtpStatus])

  useEffect(() => {
    const jwtToken = searchParams.get(QUERY_PARAMS_EXCHANGE_TOKEN_KEY)
    if (jwtToken) {
      const payload = jwtDecode<IExchangeJwtPayload>(jwtToken!)
      setCustomerPhoneNumber(payload.PhoneNumber)
    }
  }, [])

  const handleInputChange = (value: string) => {
    const filteredValue = value.replace(/[^0-9]/g, "")
    setOtp(filteredValue)
  }

  const handleResendOtp = useCallback(() => {
    resendOtpMutate()
  }, [])

  if (isAuthLoading) {
    return <LoadingOverlay />
  }

  const renderBottomSection = () => {
    switch (otpState) {
      case 1:
        return (
          <Button
            variant="text"
            sx={ChangeNumberText}
            onClick={handleResendOtp}
          >
            Отправить код повторно
          </Button>
        )
      case 2:
        return (
          <Typography variant="body1" sx={ResendText}>
            {`Повторно отправить код можно будет\nчерез ${timer} секунд.`}
          </Typography>
        )
      case 3:
        return (
          <Typography variant="h6" sx={RepeatCodeTitle}>
            Лимит по отправке смс с кодом исчерпан
          </Typography>
        )
      default:
        return null
    }
  }

  return (
    <Box sx={ContentContainer}>
      <Typography variant="h3" sx={AuthTitle}>
        Код подтверждения
      </Typography>
      <Typography variant="h6" sx={AuthSubtitle}>
        Код подтверждения отправлен на номер{" "}
        {formatPhoneNumber(customerPhoneNumber)}
      </Typography>
      <Typography variant="h4Bold" sx={CodeTitle}>
        Код из СМС
      </Typography>
      <StyledMuiOtp
        autoFocus
        length={6}
        onChange={handleInputChange}
        value={otp}
        error={otpResult?.errorDetails?.code === ApiErrorCodes.OTP_ATTEMPT_FAILED}
        TextFieldsProps={{
          variant: "standard",
          type: "number",
        }}
      />
      {renderBottomSection()}
    </Box>
  )
}

export default AuthOtpConfirmation
